import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";
import parse from "html-react-parser";

import Loader from "../Loader";
import { sanitizeHtml } from "../../utils/sanitize";
import { questionImageUrl } from "../../utils/assets";
import {
  DEMO_DURATION_MS,
  DEMO_QUESTION_LIMIT,
  formatDemoClock,
  optionId,
  optionText,
  saveDemoResult,
  scoreDemoAnswers,
} from "./demoSession";

import "./Demo.css";

const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F", "G"];
const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api/v1";

function renderHtml(value) {
  if (!value) return null;
  return parse(sanitizeHtml(String(value)));
}

const DemoQuiz = () => {
  const history = useHistory();
  const finishedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(DEMO_DURATION_MS);

  const finishPractice = useCallback(
    (nextQuestions, nextAnswers, remainingMs) => {
      if (finishedRef.current) return;
      finishedRef.current = true;

      const quizQuestions = nextQuestions || questions;
      const quizAnswers = nextAnswers || answers;
      const remaining = remainingMs === undefined ? timeLeft : remainingMs;
      const score = scoreDemoAnswers(quizQuestions, quizAnswers);
      // Study mode: keep the score, drop correctAns so the result page (and
      // sessionStorage) cannot reveal answers without a subscription.
      const lockedQuestions = quizQuestions.map((question) => {
        const { correctAns, explanation, description, ...safeQuestion } = question || {};
        return safeQuestion;
      });
      const payload = {
        questions: lockedQuestions,
        selectedAnswers: quizAnswers,
        score,
        timeUsedMs: DEMO_DURATION_MS - remaining,
        totalDurationMs: DEMO_DURATION_MS,
        finishedAt: Date.now(),
      };
      saveDemoResult(payload);
      history.replace("/demo/result", payload);
    },
    [answers, history, questions, timeLeft]
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(`${API_BASE}/demo-questions`);
        if (cancelled) return;

        if (!data?.success) {
          setQuestions([]);
          setError(data?.message || "Unable to fetch demo questions.");
          return;
        }

        const pool = Array.isArray(data.data) ? data.data.slice(0, DEMO_QUESTION_LIMIT) : [];
        setQuestions(pool);
        setAnswers({});
        setCurrentIndex(0);
        setTimeLeft(DEMO_DURATION_MS);

        if (pool.length === 0) {
          setError("No demo questions are available right now. Please try again later.");
        }
      } catch (err) {
        if (cancelled) return;
        setQuestions([]);
        setError(
          err?.response?.data?.message || "Unable to fetch demo questions."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (loading || questions.length === 0 || error) return undefined;

    const timer = setInterval(() => {
      setTimeLeft((value) => Math.max(value - 1000, 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [error, loading, questions.length]);

  useEffect(() => {
    if (!loading && questions.length > 0 && !error && timeLeft <= 0) {
      finishPractice(questions, answers, 0);
    }
  }, [answers, error, finishPractice, loading, questions, timeLeft]);

  if (loading) return <Loader />;

  if (error || questions.length === 0) {
    return (
      <div className="demo-page demo-page--compact">
        <div className="demo-page__card demo-page__empty">
          <span className="demo-page__hero-icon">
            <i className="fas fa-inbox" />
          </span>
          <h2>Demo unavailable</h2>
          <p>{error || "No demo questions found."}</p>
          <div className="demo-page__actions">
            <Link className="demo-page__btn demo-page__btn--green" to="/demo">
              Back to demo intro
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const current = questions[currentIndex];
  const questionId = String(current?._id || "");
  const selected = answers[questionId];
  const urgent = timeLeft <= 60 * 1000;

  const selectAnswer = (answerId) => {
    if (!questionId || !answerId) return;
    setAnswers((prev) => ({ ...prev, [questionId]: String(answerId) }));
  };

  const goPrevious = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const goNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      return;
    }
    finishPractice();
  };

  return (
    <Fragment>
      <div className="demo-quiz">
        <div className="demo-quiz__topbar">
          <div className="demo-quiz__brand">
            <span className="demo-quiz__kicker">Demo Exam</span>
            <strong>
              Question {currentIndex + 1} of {questions.length}
            </strong>
          </div>
          <div className={`demo-quiz__timer${urgent ? " is-urgent" : ""}`}>
            <i className="fas fa-clock" /> {formatDemoClock(timeLeft)}
          </div>
        </div>

        <div className="demo-quiz__body">
          <div className="demo-quiz__progress">
            <span
              style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
            />
          </div>

          {current.subjectInfo?.title && (
            <p className="demo-quiz__subject">
              <i className="fas fa-book" /> {current.subjectInfo.title}
            </p>
          )}

          {current.passage && (
            <div className="demo-quiz__passage">{renderHtml(current.passage)}</div>
          )}

          <div className="demo-quiz__card">
            <div className="demo-quiz__question-head">
              <span className="demo-quiz__question-no">{currentIndex + 1}</span>
              <div className="demo-quiz__question-text">
                {current.title && (
                  <div className="demo-quiz__question-title">{renderHtml(current.title)}</div>
                )}
                {renderHtml(current.question)}
              </div>
            </div>

            {current.logo && (
              <img
                className="demo-quiz__image"
                src={questionImageUrl(current.logo)}
                alt="Question illustration"
              />
            )}

            <ul className="demo-quiz__options">
              {(current.options || []).map((option, index) => {
                const id = optionId(option);
                const isSelected = selected && selected === id;
                return (
                  <li key={id || index}>
                    <button
                      type="button"
                      className={`demo-quiz__option${isSelected ? " is-selected" : ""}`}
                      onClick={() => selectAnswer(id)}
                    >
                      <span className="demo-quiz__option-letter">
                        {OPTION_LETTERS[index] || index + 1}
                      </span>
                      <span className="demo-quiz__option-text">{renderHtml(optionText(option))}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="demo-quiz__footer">
            <button
              type="button"
              className="demo-page__btn demo-page__btn--ghost"
              onClick={goPrevious}
              disabled={currentIndex === 0}
            >
              <i className="fas fa-angle-left" /> Previous
            </button>

            <div className="demo-quiz__dots">
              {questions.map((question, index) => {
                const id = String(question._id || index);
                const answered = Boolean(answers[id]);
                return (
                  <button
                    key={id}
                    type="button"
                    className={`demo-quiz__dot${answered ? " is-answered" : ""}${
                      index === currentIndex ? " is-active" : ""
                    }`}
                    onClick={() => setCurrentIndex(index)}
                    aria-label={`Go to question ${index + 1}`}
                  />
                );
              })}
            </div>

            <button
              type="button"
              className="demo-page__btn demo-page__btn--green"
              onClick={goNext}
            >
              {currentIndex >= questions.length - 1 ? "Finish" : "Next"}{" "}
              <i className="fas fa-angle-right" />
            </button>
          </div>

          <p className="demo-quiz__hint">
            Study mode · your score is calculated locally · correct answers stay
            locked until you subscribe
          </p>
        </div>
      </div>
    </Fragment>
  );
};

export default DemoQuiz;
